import { fetchApi } from "@/lib/api/index.ts";
import { useToast } from "@/components/ui/use-toast.ts";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

export interface ChangelogEntry {
  id: string;
  title: string;
  subtitle: string | null;
  date_label: string | null;
  body: string;
  changes: string[];
  loom_url: string | null;
  is_published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  is_read?: boolean;
}

export interface AdminChangelogEntry extends ChangelogEntry {
  role_ids: string[];
  view_count: number;
  created_by: string | null;
}

export interface ChangelogInput {
  title: string;
  subtitle?: string;
  date_label?: string;
  body: string;
  changes: string[];
  loom_url?: string;
  role_ids: string[];
}

const CHANGELOG_KEY = ["changelog"] as const;
const UNREAD_KEY = ["changelog", "unread-count"] as const;
const ADMIN_KEY = ["changelog", "admin"] as const;

export const useChangelogEntries = (enabled: boolean = true) => {
  return useQuery({
    queryKey: CHANGELOG_KEY,
    queryFn: async () => {
      const { data } = await fetchApi<{ updates: ChangelogEntry[] }>({
        url: "/v1/product-updates",
        method: "GET",
      });
      return data?.updates ?? [];
    },
    enabled,
  });
};

export const useUnreadChangelogCount = (enabled: boolean = true) => {
  return useQuery({
    queryKey: UNREAD_KEY,
    queryFn: async () => {
      const { data } = await fetchApi<{ count: number }>({
        url: "/v1/product-updates/unread-count",
        method: "GET",
      });
      return data?.count ?? 0;
    },
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
};

export const useMarkChangelogViewed = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (productUpdateId: string) => {
      await fetchApi({
        url: `/v1/product-updates/${productUpdateId}/view`,
        method: "POST",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CHANGELOG_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_KEY });
    },
  });
};

export const useAdminChangelogEntries = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ADMIN_KEY,
    queryFn: async () => {
      const { data } = await fetchApi<{
        updates: AdminChangelogEntry[];
      }>({
        url: "/v1/product-updates/admin",
        method: "GET",
      });
      return data?.updates ?? [];
    },
    enabled,
  });
};

const invalidateAll = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: ADMIN_KEY });
  queryClient.invalidateQueries({ queryKey: CHANGELOG_KEY });
  queryClient.invalidateQueries({ queryKey: UNREAD_KEY });
};

export const useCreateChangelog = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (input: ChangelogInput) => {
      const { data } = await fetchApi<{ update: AdminChangelogEntry }>({
        url: "/v1/product-updates/admin",
        method: "POST",
        data: input,
      });
      return data?.update;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
      toast({
        title: "Changelog created",
        description: "Saved as a draft. Publish it when you're ready.",
      });
    },
    onError: () => {
      toast({
        title: "Failed to create changelog",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateChangelog = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({
      id,
      ...input
    }: ChangelogInput & { id: string }) => {
      const { data } = await fetchApi<{ update: AdminChangelogEntry }>({
        url: `/v1/product-updates/admin/${id}`,
        method: "PATCH",
        data: input,
      });
      return data?.update;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
      toast({ title: "Changelog updated" });
    },
    onError: () => {
      toast({
        title: "Failed to update changelog",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const usePublishChangelog = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await fetchApi<{ update: AdminChangelogEntry }>({
        url: `/v1/product-updates/admin/${id}/publish`,
        method: "POST",
      });
      return data?.update;
    },
    onSuccess: () => {
      invalidateAll(queryClient);
      toast({
        title: "Changelog published",
        description: "Targeted users will be notified.",
      });
    },
    onError: () => {
      toast({
        title: "Failed to publish changelog",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });
};

export const useDeleteChangelog = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: string) => {
      await fetchApi({
        url: `/v1/product-updates/admin/${id}`,
        method: "DELETE",
      });
    },
    onSuccess: () => {
      invalidateAll(queryClient);
      toast({ title: "Changelog deleted" });
    },
    onError: () => {
      toast({
        title: "Failed to delete changelog",
        description: "Please try again.",
        variant: "destructive",
      });
    },
  });
};
